/**
 * MUTAGIM.SHOP — Admin Agents Page
 * Gestion des comptes agents chinois (ACBuy, LeadBuy, etc.)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AdminPageWrapper } from '../components/admin/AdminPageWrapper';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Loader2,
  Plus,
  Trash2,
  RefreshCw,
  Check,
  X,
  AlertCircle,
  Wallet,
  Link2,
  Settings,
  Eye,
  EyeOff,
  MessageSquare,
  Mail,
  Bell,
  Package,
  Plug
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Logos des agents
const AGENT_LOGOS = {
  acbuy: 'https://www.acbuy.com/favicon.ico',
  leadbuy: '/images/leadbuy-logo.png',
  superbuy: 'https://www.superbuy.com/favicon.ico',
  cssbuy: 'https://www.cssbuy.com/favicon.ico',
  pandabuy: 'https://www.pandabuy.com/favicon.ico'
};

// Couleurs par statut de connexion
const STATUS_COLORS = {
  connected: 'bg-green-500/20 text-green-400 border-green-500/50',
  error: 'bg-red-500/20 text-red-400 border-red-500/50',
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  unknown: 'bg-gray-500/20 text-gray-400 border-gray-500/50'
};

export function AdminAgentsPage() {
  const { isLight } = useAdminTheme();
  const token = localStorage.getItem('admin_token');
  
  const [agents, setAgents] = useState([]);
  const [supportedAgents, setSupportedAgents] = useState({});
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [testing, setTesting] = useState(null);
  const [refreshing, setRefreshing] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Formulaire nouveau compte
  const [newAgent, setNewAgent] = useState({
    agent_type: '',
    name: '',
    email: '',
    password: '',
    api_key: '',
    is_default: false,
    low_balance_threshold: 100
  });

  // Charger les données
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [agentsRes, supportedRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/agents/admin/all`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/agents/supported`),
        fetch(`${API_URL}/api/agents/admin/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (agentsRes.ok) {
        const data = await agentsRes.json();
        setAgents(data.agents || []);
      }
      
      if (supportedRes.ok) {
        const data = await supportedRes.json();
        setSupportedAgents(data.agents || {});
      }
      
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSettings(data);
      }
      
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Ajouter un compte
  const handleAddAgent = async () => {
    if (!newAgent.agent_type || !newAgent.email || !newAgent.password) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/agents/admin/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAgent)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success('Compte ajouté ! Test de connexion en cours...');
        setAddDialogOpen(false);
        setNewAgent({
          agent_type: '',
          name: '',
          email: '',
          password: '',
          api_key: '',
          is_default: false,
          low_balance_threshold: 100
        });
        fetchData();
      } else {
        toast.error(data.detail || 'Erreur lors de l\'ajout');
      }
    } catch (err) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  // Supprimer un compte
  const handleDeleteAgent = async () => {
    if (!agentToDelete) return;
    
    try {
      const res = await fetch(`${API_URL}/api/agents/admin/${agentToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Compte supprimé');
        setDeleteDialogOpen(false);
        setAgentToDelete(null);
        fetchData();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Tester la connexion
  const handleTestConnection = async (agentId) => {
    setTesting(agentId);
    try {
      const res = await fetch(`${API_URL}/api/agents/admin/${agentId}/test-connection`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Connexion réussie !');
      } else {
        toast.error(data.error || 'Échec de la connexion');
      }
      
      fetchData();
    } catch (err) {
      toast.error('Erreur lors du test');
    } finally {
      setTesting(null);
    }
  };

  // Rafraîchir le solde
  const handleRefreshBalance = async (agentId) => {
    setRefreshing(agentId);
    try {
      const res = await fetch(`${API_URL}/api/agents/admin/${agentId}/refresh-balance`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(`Solde: ${data.balance} ${data.currency}`);
        fetchData();
      } else {
        toast.error(data.error || 'Impossible de récupérer le solde');
      }
    } catch (err) {
      toast.error('Erreur lors de la récupération du solde');
    } finally {
      setRefreshing(null);
    }
  };

  // Sauvegarder les paramètres
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${API_URL}/api/agents/admin/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (res.ok) {
        toast.success('Paramètres sauvegardés');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading) {
    return (
      <AdminPageWrapper title="Agents" subtitle="Chargement...">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        </div>
      </AdminPageWrapper>
    );
  }

  return (
    <AdminPageWrapper 
      title="Gestion des Agents" 
      subtitle="Connectez vos comptes agents chinois pour automatiser vos commandes"
    >
      <div className="space-y-6" data-testid="admin-agents-page">
        <Tabs defaultValue="accounts" className="w-full">
          <TabsList className={cn(
            "grid w-full max-w-md grid-cols-2",
            isLight ? "bg-gray-100" : "bg-zinc-800"
          )}>
            <TabsTrigger value="accounts">Comptes connectés</TabsTrigger>
            <TabsTrigger value="settings">Paramètres</TabsTrigger>
          </TabsList>

          {/* Tab: Comptes */}
          <TabsContent value="accounts" className="space-y-6 mt-6">
            {/* Bouton ajouter */}
            <div className="flex justify-between items-center">
              <div>
                <h3 className={cn("text-lg font-semibold", isLight ? "text-gray-900" : "text-white")}>
                  Comptes agents ({agents.length})
                </h3>
                <p className={cn("text-sm", isLight ? "text-gray-600" : "text-gray-400")}>
                  Gérez vos connexions aux agents ACBuy, LeadBuy et autres
                </p>
              </div>
              <Button onClick={() => setAddDialogOpen(true)} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un compte
              </Button>
            </div>

            {/* Liste des agents */}
            {agents.length === 0 ? (
              <Card className={isLight ? "bg-white border-gray-200" : "bg-zinc-900/50 border-zinc-800"}>
                <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                  <Plug className={cn("w-12 h-12 mb-4 opacity-50", isLight ? "text-gray-400" : "text-gray-600")} />
                  <p className={isLight ? "text-gray-600" : "text-gray-400"}>
                    Aucun compte agent connecté
                  </p>
                  <p className={cn("text-sm mt-1", isLight ? "text-gray-500" : "text-gray-500")}>
                    Ajoutez votre premier compte pour commencer
                  </p>
                  <Button 
                    onClick={() => setAddDialogOpen(true)} 
                    variant="outline" 
                    className="mt-4"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un compte
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {agents.map((agent) => (
                  <Card 
                    key={agent.id}
                    className={cn(
                      "relative overflow-hidden",
                      isLight ? "bg-white border-gray-200" : "bg-zinc-900/50 border-zinc-800"
                    )}
                  >
                    {agent.is_default && (
                      <div className="absolute top-0 right-0 bg-indigo-600 text-white text-xs px-2 py-0.5 rounded-bl">
                        Par défaut
                      </div>
                    )}
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-10 h-10 rounded-lg flex items-center justify-center",
                            isLight ? "bg-gray-100" : "bg-zinc-800"
                          )}>
                            <img 
                              src={AGENT_LOGOS[agent.agent_type] || '/images/agent-default.png'} 
                              alt={agent.agent_type}
                              className="w-6 h-6 object-contain"
                              onError={(e) => { e.target.src = '/images/placeholder.png'; }}
                            />
                          </div>
                          <div>
                            <CardTitle className={cn("text-base", isLight ? "text-gray-900" : "text-white")}>
                              {agent.name || supportedAgents[agent.agent_type]?.name || agent.agent_type}
                            </CardTitle>
                            <CardDescription className="text-xs">
                              {agent.email}
                            </CardDescription>
                          </div>
                        </div>
                        <Badge className={cn("border text-xs", STATUS_COLORS[agent.connection_status || 'unknown'])}>
                          {agent.connection_status === 'connected' && <Check className="w-3 h-3 mr-1" />}
                          {agent.connection_status === 'error' && <X className="w-3 h-3 mr-1" />}
                          {agent.connection_status === 'connected' ? 'Connecté' : 
                           agent.connection_status === 'error' ? 'Erreur' : 
                           agent.connection_status === 'pending' ? 'En attente' : 'Inconnu'}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Solde */}
                      <div className={cn(
                        "flex items-center justify-between p-3 rounded-lg",
                        isLight ? "bg-gray-50" : "bg-zinc-800/50"
                      )}>
                        <div className="flex items-center gap-2">
                          <Wallet className={cn("w-4 h-4", isLight ? "text-gray-500" : "text-gray-400")} />
                          <span className={cn("text-sm", isLight ? "text-gray-600" : "text-gray-400")}>
                            Solde
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {agent.last_balance !== undefined ? (
                            <span className={cn("font-semibold", isLight ? "text-gray-900" : "text-white")}>
                              {agent.last_balance?.toFixed(2)} {agent.last_balance_currency || 'CNY'}
                            </span>
                          ) : (
                            <span className={cn("text-sm", isLight ? "text-gray-500" : "text-gray-500")}>
                              Non disponible
                            </span>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6"
                            onClick={() => handleRefreshBalance(agent.id)}
                            disabled={refreshing === agent.id}
                          >
                            {refreshing === agent.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3 h-3" />
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Alerte solde bas */}
                      {agent.last_balance !== undefined && 
                       agent.last_balance < (agent.low_balance_threshold || 100) && (
                        <div className="flex items-center gap-2 p-2 rounded bg-orange-500/10 border border-orange-500/30">
                          <AlertCircle className="w-4 h-4 text-orange-400" />
                          <span className="text-xs text-orange-400">
                            Solde bas ! Seuil: {agent.low_balance_threshold || 100} CNY
                          </span>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center justify-between pt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleTestConnection(agent.id)}
                          disabled={testing === agent.id}
                          className={isLight ? "border-gray-300" : "border-zinc-700"}
                        >
                          {testing === agent.id ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-1" />
                          ) : (
                            <Link2 className="w-4 h-4 mr-1" />
                          )}
                          Tester
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          onClick={() => {
                            setAgentToDelete(agent);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tab: Paramètres */}
          <TabsContent value="settings" className="space-y-6 mt-6">
            <Card className={isLight ? "bg-white border-gray-200" : "bg-zinc-900/50 border-zinc-800"}>
              <CardHeader>
                <CardTitle className={cn("flex items-center gap-2", isLight ? "text-gray-900" : "text-white")}>
                  <Settings className="w-5 h-5" />
                  Paramètres de synchronisation
                </CardTitle>
                <CardDescription>
                  Configurez le comportement automatique des agents
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Validation QC obligatoire */}
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className={isLight ? "text-gray-900" : "text-white"}>
                      Validation QC obligatoire
                    </Label>
                    <p className={cn("text-sm", isLight ? "text-gray-500" : "text-gray-400")}>
                      Les clients doivent approuver les photos QC avant l'expédition
                    </p>
                  </div>
                  <Switch
                    checked={settings.qc_validation_required}
                    onCheckedChange={(checked) => setSettings({ ...settings, qc_validation_required: checked })}
                  />
                </div>

                {/* Notifications automatiques QC */}
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className={isLight ? "text-gray-900" : "text-white"}>
                      Notifications QC automatiques
                    </Label>
                    <p className={cn("text-sm", isLight ? "text-gray-500" : "text-gray-400")}>
                      Envoyer automatiquement les photos QC aux clients
                    </p>
                  </div>
                  <Switch
                    checked={settings.auto_notify_qc}
                    onCheckedChange={(checked) => setSettings({ ...settings, auto_notify_qc: checked })}
                  />
                </div>

                {/* Notifications tracking automatiques */}
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className={isLight ? "text-gray-900" : "text-white"}>
                      Notifications tracking automatiques
                    </Label>
                    <p className={cn("text-sm", isLight ? "text-gray-500" : "text-gray-400")}>
                      Notifier les clients des mises à jour de suivi
                    </p>
                  </div>
                  <Switch
                    checked={settings.auto_notify_tracking}
                    onCheckedChange={(checked) => setSettings({ ...settings, auto_notify_tracking: checked })}
                  />
                </div>

                {/* Canaux de notification */}
                <div className="space-y-3">
                  <Label className={isLight ? "text-gray-900" : "text-white"}>
                    Canaux de notification
                  </Label>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant={settings.notification_channels?.includes('email') ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        const channels = settings.notification_channels || [];
                        if (channels.includes('email')) {
                          setSettings({ ...settings, notification_channels: channels.filter(c => c !== 'email') });
                        } else {
                          setSettings({ ...settings, notification_channels: [...channels, 'email'] });
                        }
                      }}
                      className={settings.notification_channels?.includes('email') ? 'bg-indigo-600' : ''}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Email
                    </Button>
                    <Button
                      variant={settings.notification_channels?.includes('whatsapp') ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        const channels = settings.notification_channels || [];
                        if (channels.includes('whatsapp')) {
                          setSettings({ ...settings, notification_channels: channels.filter(c => c !== 'whatsapp') });
                        } else {
                          setSettings({ ...settings, notification_channels: [...channels, 'whatsapp'] });
                        }
                      }}
                      className={settings.notification_channels?.includes('whatsapp') ? 'bg-green-600' : ''}
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                  </div>
                </div>

                {/* Rappel QC */}
                <div className="space-y-3">
                  <Label className={isLight ? "text-gray-900" : "text-white"}>
                    Rappel validation QC (heures)
                  </Label>
                  <p className={cn("text-sm", isLight ? "text-gray-500" : "text-gray-400")}>
                    Envoyer un rappel si le client n'a pas validé après ce délai
                  </p>
                  <Input
                    type="number"
                    value={settings.qc_reminder_hours || 24}
                    onChange={(e) => setSettings({ ...settings, qc_reminder_hours: parseInt(e.target.value) })}
                    className={cn("max-w-[120px]", isLight ? "" : "bg-zinc-800 border-zinc-700")}
                  />
                </div>

                {/* Bouton sauvegarder */}
                <div className="pt-4 border-t" style={{ borderColor: isLight ? '#e5e7eb' : '#3f3f46' }}>
                  <Button
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {savingSettings ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Sauvegarder les paramètres
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog: Ajouter un compte */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className={cn(
          "max-w-md",
          isLight ? "bg-white" : "bg-zinc-900 border-zinc-800"
        )}>
          <DialogHeader>
            <DialogTitle className={isLight ? "text-gray-900" : "text-white"}>
              Ajouter un compte agent
            </DialogTitle>
            <DialogDescription className={isLight ? "text-gray-600" : "text-gray-400"}>
              Connectez votre compte pour synchroniser les commandes
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Type d'agent */}
            <div className="space-y-2">
              <Label>Agent *</Label>
              <Select 
                value={newAgent.agent_type} 
                onValueChange={(value) => setNewAgent({ ...newAgent, agent_type: value, name: supportedAgents[value]?.name || value })}
              >
                <SelectTrigger className={isLight ? "" : "bg-zinc-800 border-zinc-700"}>
                  <SelectValue placeholder="Sélectionner un agent" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(supportedAgents).map(([key, agent]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <img src={AGENT_LOGOS[key]} alt="" className="w-4 h-4" onError={(e) => { e.target.style.display = 'none'; }} />
                        {agent.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Nom personnalisé */}
            <div className="space-y-2">
              <Label>Nom du compte</Label>
              <Input
                value={newAgent.name}
                onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                placeholder="Ex: Mon compte ACBuy principal"
                className={isLight ? "" : "bg-zinc-800 border-zinc-700"}
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={newAgent.email}
                onChange={(e) => setNewAgent({ ...newAgent, email: e.target.value })}
                placeholder="votre@email.com"
                className={isLight ? "" : "bg-zinc-800 border-zinc-700"}
              />
            </div>

            {/* Mot de passe */}
            <div className="space-y-2">
              <Label>Mot de passe *</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={newAgent.password}
                  onChange={(e) => setNewAgent({ ...newAgent, password: e.target.value })}
                  placeholder="Votre mot de passe"
                  className={cn("pr-10", isLight ? "" : "bg-zinc-800 border-zinc-700")}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <p className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-500")}>
                Stocké de manière chiffrée
              </p>
            </div>

            {/* Seuil alerte solde */}
            <div className="space-y-2">
              <Label>Seuil d'alerte solde bas (CNY)</Label>
              <Input
                type="number"
                value={newAgent.low_balance_threshold}
                onChange={(e) => setNewAgent({ ...newAgent, low_balance_threshold: parseFloat(e.target.value) })}
                className={cn("max-w-[150px]", isLight ? "" : "bg-zinc-800 border-zinc-700")}
              />
            </div>

            {/* Compte par défaut */}
            <div className="flex items-center gap-2">
              <Switch
                id="is_default"
                checked={newAgent.is_default}
                onCheckedChange={(checked) => setNewAgent({ ...newAgent, is_default: checked })}
              />
              <Label htmlFor="is_default" className="text-sm">
                Définir comme compte par défaut
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddAgent} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Confirmer suppression */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className={cn(
          "max-w-sm",
          isLight ? "bg-white" : "bg-zinc-900 border-zinc-800"
        )}>
          <DialogHeader>
            <DialogTitle className={isLight ? "text-gray-900" : "text-white"}>
              Supprimer ce compte ?
            </DialogTitle>
            <DialogDescription className={isLight ? "text-gray-600" : "text-gray-400"}>
              Le compte "{agentToDelete?.name}" sera supprimé. Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleDeleteAgent} className="bg-red-600 hover:bg-red-700">
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPageWrapper>
  );
}

export default AdminAgentsPage;
